import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { gameAPI } from '../api/gameApi';
import useBrawlSession from '../hooks/useBrawlSession';
import BrawlStage from '../components/BrawlStage';
import BrawlControls from '../components/BrawlControls';
import BrawlHud from '../components/BrawlHud';
import FightOverlay, { useCountdown } from '../components/FightOverlay';
import StatChips from '../../../../shared/components/StatChips';
import HeroFigure, { normalizeLook } from '../../common/HeroFigure';
import DatumPassRequiredModal, { PASS_REQUIRED_CODE } from '../../common/DatumPassRequiredModal';
import { InviteBanner, InviteWaiting } from '../../common/InviteRoom';
import { fetchCharacters } from '../../../inventory/store/characterSlice';
import { fighterSpeed, spellMeta, worldOf } from '../engine/constants';
import { formatGram, stakePrize } from '../utils/heroArt';
import useElementHeight from '../hooks/useElementHeight';
import { haptic, hapticNotify } from '../utils/haptics';
import '../styles/arena.css';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';
import { msg, tr } from '../../../../i18n/msg';
import { characterName } from '../../../../i18n/entityText';
import { gameErrorText } from '../../common/gameErrors';

/**
 * PvP Arena (SPEC_V141_GAMES.md §3–§4):
 * lobby (hero + stake) → queue (searching) → match (countdown → fight) → result.
 * «Play with a friend»: lobby → room (invite link, waiting) → match; the friend opens /arena?room=CODE → Accept.
 * The server is authoritative: we only send inputs; everything shown comes from snapshots.
 */

const errMsg = (e) => e?.response?.data?.message || e?.response?.data?.error || e?.message
  || i18n.t('common.failed_to_load', 'Failed to load');
const REASONS = {
  KO: msg('arena.reason.ko', 'Knock-out'),
  TIME: msg('arena.reason.time', 'Time is up'),
  FORFEIT: msg('arena.reason.forfeit', 'Forfeit'),
  DISCONNECT: msg('arena.reason.disconnect', 'Disconnect'),
  DRAW: msg('arena.reason.draw', 'Draw'),
};
const RESULT_DELAY_MS = 1600;
const NO_CHARACTERS = [];

function GramIcon() {
  return <img className="arena-gram" src="/gram.png" alt="GRAM" />;
}

function BackButton({ onClick }) {
  const { t } = useTranslation();
  return (
    <button type="button" className="arena-back" onClick={onClick} aria-label={t('common.back', 'Back')}>
      <svg viewBox="0 0 24 24"><path d="M15 5 8 12l7 7" /></svg>
    </button>
  );
}

function formatAgo(t, value) {
  const ts = typeof value === 'number' ? value : Date.parse(value);
  if (!Number.isFinite(ts)) return '';
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return t('time.just_now', 'just now');
  if (s < 3600) return t('time.minutes_ago', '{{n}}m ago', { n: Math.floor(s / 60) });
  if (s < 86400) return t('time.hours_ago', '{{n}}h ago', { n: Math.floor(s / 3600) });
  return t('time.days_ago', '{{n}}d ago', { n: Math.floor(s / 86400) });
}

// ── Lobby ───────────────────────────────────────────────────────────────────

function HeroCard({ hero, look, selected, onSelect }) {
  const { t } = useTranslation();
  const sp = spellMeta(hero.spell, hero.character);
  const disabled = !!hero.onMission;
  return (
    <button
      type="button"
      className={`arena-hero${selected ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}`}
      disabled={disabled}
      onClick={() => onSelect(hero.id)}
    >
      <div className="arena-hero__avatar">
        <HeroFigure look={look} character={hero.character} size={50} pose="idle" animated={selected} />
      </div>
      <div className="arena-hero__info">
        <div className="arena-hero__top">
          <span className="arena-hero__class">{hero.character ? characterName(t, hero.character) : t('common.hero', 'Hero')}</span>
          {disabled && <span className="arena-hero__busy">{t('arena.on_mission', 'On mission')}</span>}
        </div>
        <StatChips str={hero.str} agi={hero.agi} int={hero.int} power={hero.power} size="xs" />
        <div className="arena-hero__spell"><span>{sp.icon}</span>{tr(t, sp.name)}</div>
      </div>
    </button>
  );
}

function Lobby({
  info, loading, error, heroId, setHeroId, stake, setStake, lookById, onFind, onInvite, onRetry, onPassRequired, banner,
}) {
  const { t } = useTranslation();
  if (loading && !info) return <div className="arena-center"><div className="arena-spinner" /></div>;
  if (error && !info) {
    return (
      <div className="arena-center">
        <div className="arena-muted">{error}</div>
        <button type="button" className="app-btn app-btn--purple app-btn--sm arena-retry" onClick={onRetry}>{t('common.retry', 'Retry')}</button>
      </div>
    );
  }
  if (!info) return null;
  if (info.enabled === false) {
    return <div className="arena-center"><div className="arena-muted">{t('arena.closed', 'The Arena is closed right now. Come back later!')}</div></div>;
  }

  const heroes = info.heroes || [];
  const stakes = info.stakes || ['0'];
  const balance = Number(info.tonBalance) || 0;
  const commission = Number(info.commissionPercent) || 0;
  const hero = heroes.find((h) => h.id === heroId);
  const stakeNum = Number(stake) || 0;
  const prize = stakePrize(stakeNum, commission);
  const canAfford = stakeNum <= balance;
  const canFind = !!hero && !hero.onMission && canAfford;
  // GRAM stakes are DatumPass-only (free training is open to everyone)
  const hasPass = info.datumPassActive === true;

  return (
    <div className="arena-lobby">
      {banner}
      <section className="arena-section">
        <h3 className="arena-section__title">{t('arena.choose_fighter', 'Choose your fighter')}</h3>
        {heroes.length === 0 ? (
          <div className="arena-empty">{t('arena.no_heroes', 'Complete a hero (all 6 parts → «Complete») to enter the Arena.')}</div>
        ) : (
          <div className="arena-heroes">
            {heroes.map((h) => (
              <HeroCard key={h.id} hero={h} look={h.look || lookById[h.id]} selected={h.id === heroId} onSelect={setHeroId} />
            ))}
          </div>
        )}
      </section>

      <section className="arena-section">
        <div className="arena-section__row">
          <h3 className="arena-section__title">{t('arena.stake', 'Stake')}</h3>
          <span className="arena-balance"><GramIcon />{formatGram(balance)} GRAM</span>
        </div>
        <div className="arena-stakes">
          {stakes.map((s) => {
            const n = Number(s) || 0;
            const tooMuch = n > balance;
            const locked = n > 0 && !hasPass;
            return (
              <button
                key={s}
                type="button"
                className={`arena-stake${String(stake) === String(s) ? ' is-selected' : ''}${n === 0 ? ' arena-stake--free' : ''}`}
                disabled={tooMuch && !locked}
                onClick={() => (locked ? onPassRequired() : setStake(String(s)))}
              >
                {n === 0 ? t('arena.free', 'Free') : (<><GramIcon />{formatGram(n)}</>)}
                {locked && <span className="stake-lock" aria-label={t('arena.pass_required_aria', 'DatumPass required')}>🔒</span>}
              </button>
            );
          })}
        </div>
        <div className="arena-prize">
          {stakeNum === 0 ? (
            <span>{t('arena.training_fight', 'Training fight — no GRAM at stake')}</span>
          ) : (
            <>
              <span>{t('arena.win', 'Win')}</span>
              <strong><GramIcon />{formatGram(prize)} GRAM</strong>
              <span className="arena-muted">{t('arena.fee', '({{pct}}% fee)', { pct: commission })}</span>
            </>
          )}
        </div>
      </section>

      <details className="arena-rules">
        <summary>{t('arena.rules.title', 'How to fight')}</summary>
        <ul>
          <li>{t('arena.rules.move', '◀ ▶ move · ▲ jump onto ledges · ▼ drop down through a ledge')}</li>
          <li>{t('arena.rules.attack', '👊 Attack — 3-hit combo, the 3rd hit breaks guard; you can attack in the air')}</li>
          <li>{t('arena.rules.block', '🛡 Block (hold, on the ground) — cuts damage to about a third and pushes the attacker back; holding too long breaks your guard')}</li>
          <li>{t('arena.rules.magic', "✨ Magic at 100 mana — your hero's spell; a hit interrupts the cast")}</li>
          <li>{t('arena.rules.pickups', 'Grab HEALTH / MANA / RAGE pickups (punch them to knock them away), lure the enemy to the 💥 barrels')}</li>
          <li>{t('arena.rules.time', '90 s: K.O. wins, otherwise more HP % wins. Stake is refunded on a draw.')}</li>
        </ul>
      </details>

      <div className="arena-cta">
        <button type="button" className="app-btn app-btn--green" disabled={!canFind} onClick={onFind}>
          ⚔ {t('arena.find_opponent', 'Find opponent')}
        </button>
        <button type="button" className="app-btn app-btn--blue inv-friend-btn" disabled={!canFind} onClick={onInvite}>
          👥 {t('pvp.play_with_friend', 'Play with a friend')}
        </button>
        {!canAfford && <div className="arena-hint arena-hint--error">{t('pvp.not_enough_gram', 'Not enough GRAM for this stake')}</div>}
        {!hasPass && <div className="arena-hint">🔒 {t('pvp.stakes_need_pass', 'GRAM stakes need an active DatumPass')}</div>}
      </div>

      {(info.recent || []).length > 0 && (
        <section className="arena-section">
          <h3 className="arena-section__title">{t('arena.recent', 'Recent matches')}</h3>
          <div className="arena-recent">
            {info.recent.map((m) => (
              <div key={m.id} className={`arena-recent__row ${m.won ? 'is-win' : m.won === false && m.reason !== 'DRAW' ? 'is-loss' : 'is-draw'}`}>
                <span className="arena-recent__res">
                  {m.won ? t('arena.res.win', 'WIN') : m.reason === 'DRAW' ? t('arena.res.draw', 'DRAW') : t('arena.res.loss', 'LOSS')}
                </span>
                <span className="arena-recent__opp">{t('arena.vs', 'vs {{name}}', { name: m.opponentName || t('arena.bot', 'Bot') })}</span>
                <span className="arena-recent__stake">{Number(m.stake) > 0 ? `${formatGram(m.stake)} GRAM` : t('arena.free', 'Free')}</span>
                <span className="arena-recent__pay">{Number(m.payout) > 0 ? `+${formatGram(m.payout)}` : ''}</span>
                <span className="arena-recent__time">{formatAgo(t, m.endedAt)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ── Searching ───────────────────────────────────────────────────────────────

function Searching({
  queue, stake, startedAt, status, onCancel, title,
}) {
  const { t } = useTranslation();
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const waited = queue?.waitedSec ?? Math.floor((Date.now() - startedAt) / 1000);
  const bracket = queue?.bracket;
  const botIn = queue?.botAfterSec != null ? Math.max(0, Math.ceil(Number(queue.botAfterSec) - waited)) : null;
  return (
    <div className="arena-search">
      <div className="arena-radar">
        <div className="arena-radar__sweep" />
        <div className="arena-radar__ring arena-radar__ring--1" />
        <div className="arena-radar__ring arena-radar__ring--2" />
        <div className="arena-radar__dot" />
      </div>
      <div className="arena-search__title">
        {status === 'connecting' || status === 'reconnecting'
          ? t('pvp.connecting', 'Connecting…')
          : (title || t('arena.searching', 'Searching for an opponent…'))}
      </div>
      <div className="arena-search__time">{Math.floor(waited / 60)}:{String(waited % 60).padStart(2, '0')}</div>
      <div className="arena-search__meta">
        <span>{t('arena.stake_value', 'Stake: {{stake}}', { stake: Number(stake) > 0 ? `${formatGram(stake)} GRAM` : t('arena.free', 'Free') })}</span>
        {bracket && <span>{t('arena.opponents_bracket', 'Opponents ⚡{{min}}–{{max}}', { min: bracket.min, max: bracket.max })}</span>}
        {botIn !== null && <span>{t('arena.bot_in', 'Training bot in {{n}}s', { n: botIn })}</span>}
      </div>
      <button type="button" className="app-btn app-btn--purple arena-search__cancel" onClick={onCancel}>{t('common.cancel', 'Cancel')}</button>
    </div>
  );
}

// ── Result ──────────────────────────────────────────────────────────────────

function Result({
  end, you, stake, onRematch, onBack,
}) {
  const { t } = useTranslation();
  const draw = end.winner === null || end.winner === undefined || end.reason === 'DRAW';
  const win = !draw && end.winner === you;
  const title = draw ? t('arena.result.draw', 'DRAW') : win ? t('arena.result.victory', 'VICTORY!') : t('arena.result.defeat', 'DEFEAT');
  const payout = Number(end.payout) || 0;
  const refund = Number(end.refund) || 0;
  return (
    <div className={`arena-result ${draw ? 'is-draw' : win ? 'is-win' : 'is-loss'}`}>
      <div className="arena-result__burst">
        <div className="arena-result__title">{title}</div>
      </div>
      <div className="arena-result__reason">{tr(t, REASONS[end.reason]) || end.reason}</div>
      {payout > 0 && <div className="arena-result__money is-plus"><GramIcon />+{formatGram(payout)} GRAM</div>}
      {refund > 0 && <div className="arena-result__money"><GramIcon />{t('arena.refunded', '{{amount}} GRAM refunded', { amount: formatGram(refund) })}</div>}
      {!win && !draw && Number(stake) > 0 && payout === 0 && refund === 0 && (
        <div className="arena-result__money is-minus"><GramIcon />−{formatGram(stake)} GRAM</div>
      )}
      <div className="arena-result__actions">
        <button type="button" className="app-btn app-btn--green" onClick={onRematch}>⚔ {t('arena.rematch', 'Rematch')}</button>
        <button type="button" className="app-btn app-btn--purple" onClick={onBack}>{t('arena.back_to_lobby', 'Back to lobby')}</button>
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function ArenaPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const characters = useSelector((s) => s.characters?.list) || NO_CHARACTERS;

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [heroId, setHeroId] = useState(null);
  const [stake, setStake] = useState('0');
  const [phase, setPhase] = useState('lobby'); // lobby | search | room | fight
  const [searchParams, setSearchParams] = useSearchParams();
  const roomCode = searchParams.get('room');
  const [invite, setInvite] = useState(null); // friend's side: { loading, data, error }
  const [joining, setJoining] = useState(null); // { stake } while joining a friend's room
  const [searchStartedAt, setSearchStartedAt] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [hud, setHud] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [passModal, setPassModal] = useState(false);
  const toastTimer = useRef(null);

  const lookById = useMemo(() => {
    const m = {};
    characters.forEach((c) => { m[c.id] = normalizeLook(c); });
    return m;
  }, [characters]);
  const [hudRef, hudH] = useElementHeight();
  const [dockRef, dockH] = useElementHeight();

  const hero = (info?.heroes || []).find((h) => h.id === heroId) || null;
  const speed = hero ? fighterSpeed(hero) : 260;
  const session = useBrawlSession({ speed });
  const {
    match, end, queue, room, status, error, engine, start, stop, socket,
  } = session;
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const showToast = useCallback((text) => {
    clearTimeout(toastTimer.current);
    setToast({ text, id: Date.now() });
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const loadInfo = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await gameAPI.getPvpInfo();
      setInfo(data);
      setHeroId((cur) => {
        const list = data?.heroes || [];
        if (cur && list.some((h) => h.id === cur && !h.onMission)) return cur;
        const best = [...list].filter((h) => !h.onMission).sort((a, b) => (b.power || 0) - (a.power || 0))[0];
        return best?.id ?? null;
      });
      setStake((cur) => {
        const list = (data?.stakes || ['0']).map(String);
        return list.includes(String(cur)) ? String(cur) : list[0];
      });
    } catch (e) {
      setLoadError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInfo();
    if (!characters.length) dispatch(fetchCharacters());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const findMatch = useCallback(() => {
    if (!heroId) return;
    if (Number(stake) > 0 && info?.datumPassActive !== true) {
      setPassModal(true);
      return;
    }
    haptic('medium');
    setPhase('search');
    setShowResult(false);
    setHud(null);
    setSearchStartedAt(Date.now());
    start((sock) => sock.joinQueue(heroId, stake));
  }, [heroId, stake, start, info]);

  // ── play with a friend ──
  useEffect(() => {
    if (!roomCode) {
      setInvite(null);
      return undefined;
    }
    let alive = true;
    setInvite({ loading: true });
    gameAPI.getRoom(roomCode)
      .then((data) => {
        if (!alive) return;
        if (data?.game === 'DURAK') {
          navigate(`/durak?room=${encodeURIComponent(roomCode)}`, { replace: true });
          return;
        }
        setInvite({ data });
      })
      .catch((e) => {
        if (!alive) return;
        setInvite({ error: e?.response?.status === 404 ? gameErrorText(t, { code: 'ROOM_NOT_FOUND' }) : errMsg(e) });
      });
    return () => { alive = false; };
  }, [roomCode, navigate, t]);

  const dismissInvite = useCallback(() => {
    setInvite(null);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const inviteFriend = useCallback(() => {
    if (!heroId) return;
    if (Number(stake) > 0 && info?.datumPassActive !== true) {
      setPassModal(true);
      return;
    }
    haptic('medium');
    setPhase('room');
    setShowResult(false);
    setHud(null);
    start((sock) => sock.createRoom(heroId, stake));
  }, [heroId, stake, start, info]);

  const acceptInvite = useCallback(() => {
    const data = invite?.data;
    if (!heroId || !data) return;
    if (Number(data.stake) > 0 && info?.datumPassActive !== true) {
      setPassModal(true);
      return;
    }
    haptic('medium');
    setJoining({ stake: data.stake });
    setPhase('search');
    setShowResult(false);
    setHud(null);
    setSearchStartedAt(Date.now());
    dismissInvite();
    start((sock) => sock.joinRoom(data.code, heroId));
  }, [invite, heroId, info, start, dismissInvite]);

  const backToLobby = useCallback(() => {
    stop();
    setJoining(null);
    setPhase('lobby');
    setShowResult(false);
    setHud(null);
    loadInfo();
  }, [stop, loadInfo]);

  const cancelSearch = useCallback(() => {
    socket()?.leaveQueue();
    backToLobby();
  }, [socket, backToLobby]);

  const cancelRoom = useCallback(() => {
    socket()?.cancelRoom();
    backToLobby();
  }, [socket, backToLobby]);

  // leaving the page with an open invite → close the room (nobody would be there to play)
  useEffect(() => () => {
    if (phaseRef.current === 'room') socket()?.cancelRoom();
  }, [socket]);

  // match found → fight
  useEffect(() => {
    if (match) {
      setPhase('fight');
      hapticNotify('success');
    }
  }, [match]);

  // end → short K.O./TIME banner, then the result screen
  useEffect(() => {
    if (!end) return undefined;
    const win = end.winner === match?.you;
    hapticNotify(end.winner == null ? 'warning' : win ? 'success' : 'error');
    const id = setTimeout(() => setShowResult(true), RESULT_DELAY_MS);
    return () => clearTimeout(id);
  }, [end, match]);

  // server errors
  useEffect(() => {
    if (!error) return;
    if (error.code === PASS_REQUIRED_CODE) {
      setStake('0');
      setPassModal(true);
    } else {
      showToast(gameErrorText(t, error));
    }
    if ((phase === 'search' || phase === 'room') && !match) backToLobby();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  // connection failed
  useEffect(() => {
    if (status !== 'failed') return;
    if ((phase === 'search' || phase === 'room') && !match) {
      showToast(t('arena.connect_failed', 'Could not connect to the Arena'));
      backToLobby();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const me = match?.fighters?.find((f) => f.idx === match.you) || null;
  const opp = match?.fighters?.find((f) => f.idx !== match.you) || null;
  const startsAt = match ? match.receivedAt + (Number(match.startsInMs) || 0) : null;
  const countdownLeft = useCountdown(startsAt);
  // own look fallback (the server sends `look` for every fighter in `match`, §11.2)
  const looks = useMemo(() => (match && lookById[heroId] ? { [match.you]: lookById[heroId] } : null), [match, lookById, heroId]);
  const world = useMemo(() => worldOf(match), [match]);
  const ownFrame = hud?.f.find((f) => f.i === match?.you);
  const mySpell = me ? spellMeta(me.spell, me.character) : null;
  const fighting = phase === 'fight' && match && !end;

  const onEvent = useCallback((e) => {
    if (!match) return;
    if (e.e === 'hit' && e.v === match.you) haptic(e.crit ? 'heavy' : 'medium');
    if (e.e === 'boom') haptic('heavy');
    if (e.e === 'land' && e.a === match.you) haptic('soft');
  }, [match]);

  const banner = end ? (end.reason === 'TIME' ? t('arena.banner.time', 'TIME!')
    : end.reason === 'KO' ? t('arena.banner.ko', 'K.O.!')
      : end.reason === 'DRAW' ? t('arena.result.draw', 'DRAW') : t('arena.banner.finish', 'FINISH')) : null;

  const leave = () => {
    if (fighting) setConfirmLeave(true);
    else if (phase === 'search') cancelSearch();
    else if (phase === 'room') cancelRoom();
    else if (phase === 'fight') backToLobby();
    else navigate('/lair', { state: { tab: 'Arena' } });
  };

  return (
    <div className={`arena-page arena-page--${phase}`}>
      {phase !== 'fight' && (
        <div className="arena-header">
          <BackButton onClick={leave} />
          <div className="arena-header__title">⚔ {t('arena.header', 'ARENA')}</div>
          <div className="arena-header__spacer" />
        </div>
      )}

      {phase === 'lobby' && (
        <div className="arena-scroll">
          <Lobby
            info={info}
            loading={loading}
            error={loadError}
            heroId={heroId}
            setHeroId={setHeroId}
            stake={stake}
            setStake={setStake}
            lookById={lookById}
            onFind={findMatch}
            onInvite={inviteFriend}
            onRetry={loadInfo}
            onPassRequired={() => setPassModal(true)}
            banner={invite && (
              <InviteBanner
                invite={invite.data}
                loading={invite.loading}
                error={invite.error}
                onAccept={acceptInvite}
                onDismiss={dismissInvite}
                acceptDisabled={!hero || !!hero.onMission || Number(invite.data?.stake) > (Number(info?.tonBalance) || 0)}
                acceptHint={(() => {
                  if (!hero || hero.onMission) return t('arena.pick_fighter_first', 'Pick a free fighter below first');
                  if (Number(invite.data?.stake) > (Number(info?.tonBalance) || 0)) return t('pvp.not_enough_gram', 'Not enough GRAM for this stake');
                  return null;
                })()}
              />
            )}
          />
        </div>
      )}

      {phase === 'search' && (
        <Searching
          queue={queue}
          stake={joining ? joining.stake : stake}
          startedAt={searchStartedAt}
          status={status}
          onCancel={cancelSearch}
          title={joining ? t('pvp.joining_friend', 'Joining your friend…') : null}
        />
      )}

      {phase === 'room' && (
        <InviteWaiting game="arena" room={room} connection={status} onCancel={cancelRoom} />
      )}

      {phase === 'fight' && match && !showResult && (
        <div className="arena-fight">
          <div className="arena-fight__stage">
            <BrawlStage
              engine={engine}
              fighters={match.fighters}
              you={match.you}
              world={world}
              looks={looks}
              theme="arena"
              fit="fill"
              reserveTop={hudH}
              reserveBottom={dockH}
              slowmoOnKo
              onHud={setHud}
              onEvent={onEvent}
            />
            <FightOverlay startsAt={startsAt} banner={banner} />
            {(status === 'reconnecting' || status === 'connecting') && !end && (
              <div className="arena-banner">{t('pvp.reconnecting', 'Reconnecting…')}</div>
            )}
            {status === 'failed' && !end && (
              <div className="arena-banner arena-banner--error">
                {t('pvp.connection_lost', 'Connection lost')}
                <button type="button" className="app-btn app-btn--purple app-btn--sm" onClick={backToLobby}>{t('common.back', 'Back')}</button>
              </div>
            )}
          </div>
          <div className="arena-fight__hud" ref={hudRef}>
            <div className="arena-fight__top">
              <button type="button" className="arena-flag" onClick={leave} aria-label={t('arena.give_up', 'Give up')}>🏳</button>
              <div className="arena-fight__stake">
                {Number(match.stake) > 0
                  ? (<><GramIcon />{formatGram(match.stake)} · {t('arena.win_lower', 'win {{amount}}', { amount: formatGram(match.prize ?? stakePrize(match.stake, info?.commissionPercent)) })}</>)
                  : t('arena.training', 'Training')}
              </div>
              {session.rtt != null && <div className="arena-fight__ping">{session.rtt} ms</div>}
            </div>
            <BrawlHud me={me} opp={opp} frame={hud} timeLimitSec={match.timeLimitSec} />
          </div>
          <div className="brawl-controls-dock" ref={dockRef}>
            <BrawlControls
              overlay
              onInput={session.sendInput}
              onAct={session.sendAct}
              mana={ownFrame?.mn || 0}
              guard={ownFrame?.gd}
              spell={mySpell}
              disabled={!fighting || countdownLeft > 0}
            />
          </div>
          {confirmLeave && (
            <div className="arena-confirm">
              <div className="arena-confirm__box">
                <div className="arena-confirm__title">{t('arena.give_up_q', 'Give up?')}</div>
                <div className="arena-muted">
                  {Number(match.stake) > 0 ? t('arena.lose_stake', 'You will lose your stake.') : t('arena.counts_as_loss', 'The fight counts as a loss.')}
                </div>
                <div className="arena-confirm__actions">
                  <button type="button" className="app-btn app-btn--purple app-btn--sm" onClick={() => setConfirmLeave(false)}>{t('arena.fight_on', 'Fight on')}</button>
                  <button
                    type="button"
                    className="app-btn app-btn--stop app-btn--sm"
                    onClick={() => { setConfirmLeave(false); socket()?.leave(); }}
                  >
                    {t('arena.give_up', 'Give up')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {phase === 'fight' && end && showResult && (
        <Result end={end} you={match?.you} stake={match?.stake ?? stake} onRematch={findMatch} onBack={backToLobby} />
      )}

      {toast && <div key={toast.id} className="arena-toast">{toast.text}</div>}

      {passModal && (
        <DatumPassRequiredModal
          onClose={() => setPassModal(false)}
          onPlayFree={() => { setStake('0'); setPassModal(false); }}
          onPurchased={loadInfo}
        />
      )}
    </div>
  );
}
